import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";

import { HelperBotType, HelperBotTypeLabels } from "../../constants/enum/enum";
import { HelperBots } from "../../constants/helper";
import {
  convertDateTime,
  toastInfo,
  toastSuccess,
  toUSDFormat,
} from "../../utils/utils";

import runTimeImg from "../../assets/imgs/crime/clockwise.png";
import loadingArrowImg from "../../assets/imgs/helperbot/loading-arrow.png";
import helperImg from "../../assets/imgs/slot/helperbot.png";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import config from "../../config/config";
import { CONFIRMATION_DELAY } from "../../constants/const";
import { BOOZE_LIMITS, NARCS_LIMITS } from "../../constants/game";
import { Messages } from "../../constants/messages";
import { getHelperCreditAllowance } from "../../helper/contractFunctions/MafiaHelperCredit";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import useWallet from "../../hook/useWallet";
import {
  approveHelperCreditAction,
  getUserHelperBotInfo,
  startBoozeBotAction,
  startBulletDealerBotAction,
  startCarBotAction,
  startCrimeBotAction,
  startKSBotAction,
  startNarcsBotAction,
} from "../../reducers/game.slice";
import { getHelperCreditBalance } from "../../reducers/profile.slice";

import useStyles from "./HelperBotStartPopupStyles";

interface HelperBotStartPopupProps {
  currentSelectedBot: HelperBotType;
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const HelperBotStartPopup: React.FC<HelperBotStartPopupProps> = ({
  currentSelectedBot,
  openModal,
  setOpenModal,
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { account } = useWallet();

  const { helperCreditBalance, myProfile } = useAppSelector(
    (state) => state.profile
  );
  const { signMsg, signature } = useAppSelector((state) => state.auth);

  const [currentHelperCreditCount, setCurrentHelperCreditCount] = useState(0);
  const [attemptCount, setAttemptCount] = useState(1);

  const handleClose = () => {
    setOpenModal(false);
  };

  const currentHelperBot = HelperBots.find(
    (bot) => bot.name === HelperBotTypeLabels[currentSelectedBot]
  );

  const handleStartBot = async () => {
    if (helperCreditBalance < currentHelperCreditCount) {
      toastInfo(Messages.HELPER_BOT.NOT_ENOUGH_CREDITS);
      return;
    }

    const startBot = () => {
      if (currentSelectedBot === HelperBotType.CrimeBot) {
        dispatchTxAction(
          dispatch,
          startCrimeBotAction({
            account,
            attemptCount,
          }),
          () => {
            handleClose();
            toastSuccess(Messages.HELPER_BOT.CRIME_BOT.STARTED_SUCCESS);
            dispatch(getUserHelperBotInfo(account));
            dispatch(getHelperCreditBalance({ account, signMsg, signature }));
          },
          CONFIRMATION_DELAY
        );
      } else if (currentSelectedBot === HelperBotType.GrandTheftAutoBot) {
        dispatchTxAction(
          dispatch,
          startCarBotAction({ account, attemptCount }),
          () => {
            handleClose();
            toastSuccess(Messages.HELPER_BOT.CAR_BOT.STARTED_SUCCESS);
            dispatch(getUserHelperBotInfo(account));
            dispatch(getHelperCreditBalance({ account, signMsg, signature }));
          },
          CONFIRMATION_DELAY
        );
      } else if (currentSelectedBot === HelperBotType.ShootingPracticeBot) {
        dispatchTxAction(
          dispatch,
          startKSBotAction({ account, attemptCount }),
          () => {
            handleClose();
            toastSuccess(Messages.HELPER_BOT.KS_BOT.STARTED_SUCCESS);
            dispatch(getUserHelperBotInfo(account));
            dispatch(getHelperCreditBalance({ account, signMsg, signature }));
          },
          CONFIRMATION_DELAY
        );
      } else if (currentSelectedBot === HelperBotType.BoozeSmugglingBot) {
        if (BOOZE_LIMITS[myProfile.rankNumber || 0] === 0) {
          toastInfo(Messages.HELPER_BOT.BOOZE_BOT.UNABLE_START);
          return;
        }

        dispatchTxAction(
          dispatch,
          startBoozeBotAction({ account, attemptCount }),
          () => {
            handleClose();
            toastSuccess(Messages.HELPER_BOT.BOOZE_BOT.STARTED_SUCCESS);
            dispatch(getUserHelperBotInfo(account));
            dispatch(getHelperCreditBalance({ account, signMsg, signature }));
          },
          CONFIRMATION_DELAY
        );
      } else if (currentSelectedBot === HelperBotType.NarcoticsSmugglingBot) {
        if (NARCS_LIMITS[myProfile.rankNumber || 0] === 0) {
          toastInfo(Messages.HELPER_BOT.NARCS_BOT.UNABLE_START);
          return;
        }

        dispatchTxAction(
          dispatch,
          startNarcsBotAction({ account, attemptCount }),
          () => {
            handleClose();
            toastSuccess(Messages.HELPER_BOT.NARCS_BOT.STARTED_SUCCESS);
            dispatch(getUserHelperBotInfo(account));
            dispatch(getHelperCreditBalance({ account, signMsg, signature }));
          },
          CONFIRMATION_DELAY
        );
      } else if (currentSelectedBot === HelperBotType.BulletDealerBot) {
        dispatchTxAction(
          dispatch,
          startBulletDealerBotAction({ account, attemptCount }),
          () => {
            handleClose();
            toastSuccess(Messages.HELPER_BOT.BULLET_DEALER_BOT.STARTED_SUCCESS);
            dispatch(getUserHelperBotInfo(account));
            dispatch(getHelperCreditBalance({ account, signMsg, signature }));
          },
          CONFIRMATION_DELAY
        );
      }
    };

    const allowance = await getHelperCreditAllowance(
      account,
      config.helperBotAddress
    );

    if (allowance > currentHelperCreditCount) {
      startBot();
    } else {
      dispatchTxAction(
        dispatch,
        approveHelperCreditAction({
          account,
          creditAmount: 10 ** 18,
          spender: config.helperBotAddress,
        }),
        () => {
          startBot();
        },
        CONFIRMATION_DELAY
      );
    }
  };

  useEffect(() => {
    if (currentHelperBot)
      setCurrentHelperCreditCount(currentHelperBot.helperBotCount[0]);
  }, [currentHelperBot]);

  useEffect(() => {
    if (!currentHelperBot) return;

    const attemptCount = currentHelperCreditCount / currentHelperBot.credits;

    setAttemptCount(attemptCount);
  }, [currentHelperCreditCount, currentHelperBot]);

  if (!currentHelperBot) return <Box>Error: No HelperBot found</Box>;

  return (
    <Dialog open={openModal} onClose={handleClose} className={classes.body}>
      <Box className={classes.modalContent}>
        <Box className={classes.closeIconBody}>
          <Button onClick={handleClose}>
            <CloseIcon htmlColor="gray" />
          </Button>
        </Box>

        <Box className={classes.contentBody}>
          <Box component={"img"} src={helperImg} className={classes.logoImg} />
          <Box className={classes.titleText}>
            {HelperBotTypeLabels[currentSelectedBot]}
          </Box>

          <Select
            value={currentHelperCreditCount}
            onChange={(e) =>
              setCurrentHelperCreditCount(e.target.value as number)
            }
            className={classes.creditSelect}
            MenuProps={{
              classes: {
                paper: classes.menuPaper,
              },
            }}
          >
            {currentHelperBot.helperBotCount.map((count, index) => {
              return (
                <MenuItem
                  value={count}
                  className={classes.menuItem}
                  key={index}
                >
                  {count} Credits
                </MenuItem>
              );
            })}
          </Select>

          <Box className={classes.availableCredits}>
            Min. {toUSDFormat(currentHelperBot.min)} & Max.{"  "}
            {toUSDFormat(currentHelperBot.max)}
          </Box>

          <Box className={classes.description}>
            <Box className={classes.descriptionDetail}>
              <Box>
                <Box component={"img"} src={loadingArrowImg}></Box>
                Attempts:
              </Box>
              <Box>{currentHelperCreditCount / currentHelperBot.credits}</Box>
            </Box>
            <Box className={classes.descriptionDetail}>
              <Box>
                <Box component={"img"} src={runTimeImg}></Box>
                Run time:
              </Box>
              <Box className={classes.estimatedTimeCosting}>
                {convertDateTime(
                  (currentHelperCreditCount / currentHelperBot.credits) *
                    currentHelperBot.attemptDuration *
                    60
                )}
              </Box>
            </Box>
          </Box>

          <Button
            variant="contained"
            className={classes.confirmBut}
            onClick={handleStartBot}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default HelperBotStartPopup;
